import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import API_ENDPOINTS from "../constants/api-endpoints";
import { TIndustry, TTerminology } from "../types";

type IndustryListParams = { page?: number; limit?: number; terminology?: string };
type SaveCallbacks = { onSuccess?: (res: any) => void; onError?: (err: any) => void };

export const useIndustryList = (qData: IndustryListParams = { limit: 200, terminology: "1" }) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.INDUSTRY, qData],
    queryFn: () => client.get(API_ENDPOINTS.INDUSTRY, { params: qData }),
    select: (res: any) => res.data.data as TIndustry[],
    refetchOnWindowFocus: false,
  });
};

export const useIndustryDetailForEdit = (id: number | null) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.INDUSTRY, id, "forEdit"],
    queryFn: () =>
      id
        ? client.get(API_ENDPOINTS.INDUSTRY + id)
        : Promise.resolve({ data: { data: { is_active: true, sort_order: 0 } } }),
    select: (res: any) => res.data.data as TIndustry,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

export const useIndustrySave = ({ onSuccess, onError }: SaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => {
      if (data.id) return client.put(API_ENDPOINTS.INDUSTRY + data.id, data);
      return client.post(API_ENDPOINTS.INDUSTRY, data);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.INDUSTRY] });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const useIndustryTerminologyUpsert = ({ onSuccess, onError }: SaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, terminology }: { id: number; terminology: TTerminology[] }) =>
      client.put(API_ENDPOINTS.INDUSTRY_TERMINOLOGY(id), { terminology }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.INDUSTRY] });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const useIndustryDelete = ({ onSuccess, onError }: { onSuccess?: () => void; onError?: () => void } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.delete(API_ENDPOINTS.INDUSTRY + id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.INDUSTRY] });
      onSuccess?.();
    },
    onError: () => onError?.(),
  });
};
